import React, { useState, useCallback, useEffect } from "react";
import clsx from "clsx";
import {
  Button,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  makeStyles
} from "@material-ui/core";
import { BackupOutlined as BrowseIcon } from "@material-ui/icons";
import useHttpErrorHandler from "src/hooks/useHttpErrorHandler";
import * as FileApi from "../api";
import { download } from "../api";
import { downloadFile } from "../../../../utils";

const useStyles = makeStyles(_ => ({
  container: {
    marginBottom: "-32px"
  },
  inputContainer: {
    "& > *": {
      paddingRight: "0px"
    },
    width: "100%"
  },
  browseButton: {},
  chipContainer: {
    width: "calc(100% - 75px)",
    position: "relative",
    bottom: "42px",
    left: "16px"
  },
  chip: {
    maxWidth: "calc(100% - 75px)"
  },
  loading: {
    marginRight: "16px"
  }
}));

interface Props {
  label: string;
  fileId?: FileUploadId;
  isShow?: boolean;
  onChange: (fileId?: string) => void;
}

interface StateProps {
  isFetched: boolean;
  isLoading: boolean;
  isUploading: boolean;
  file?: IFileUpload;
}

const FileUpload = (props: Props) => {
  const { label, isShow, fileId: defaultFileId, onChange } = props;
  const classes = useStyles();
  const { handleError } = useHttpErrorHandler();

  const [state, setState] = useState<StateProps>({
    isFetched: false,
    isLoading: false,
    isUploading: false
  });
  const isFetched = state.isFetched;

  const containerClassName = clsx({
    [classes.container]: !!state.file && isFetched
  });

  const fetchFile = useCallback(
    async (fileId: string) => {
      setState(s => ({
        ...s,
        isFetched: false,
        isUploading: false,
        isLoading: true
      }));
      try {
        const fileResponse = await FileApi.info(fileId);
        setState({
          isFetched: true,
          isUploading: false,
          isLoading: false,
          file: fileResponse.value
        });
      } catch (e) {
        handleError(e);
        setState({ isFetched: false, isUploading: false, isLoading: false });
      }
    },
    [handleError]
  );

  const uploadFile = useCallback(
    async (file: File) => {
      setState(s => ({ ...s, isFetched: false, isUploading: true }));
      try {
        const response = await FileApi.upload([file]);
        const fileUpload = response.value.files[0];
        setState({
          isFetched: true,
          isLoading: false,
          isUploading: false,
          file: fileUpload
        });
        onChange(fileUpload.id);
      } catch (e) {
        handleError(e);
        setState({ isFetched: false, isUploading: false, isLoading: false });
      }
    },
    [handleError, onChange]
  );

  const downloadAndSave = async (fileId: string) => {
    const resp = await download(fileId);
    await downloadFile(resp);
  };

  const handleRemoveFile = useCallback(() => {
    setState({ isFetched: true, isUploading: false, isLoading: false });
    onChange(undefined);
  }, [onChange]);

  const handleSelectFile = ({
    target
  }: React.ChangeEvent<HTMLInputElement>) => {
    if (target.files) {
      uploadFile(target.files[0]);
    }
  };

  useEffect(() => {
    if (defaultFileId) {
      fetchFile(defaultFileId);
    } else {
      setState({ isFetched: true, isUploading: false, isLoading: false });
    }
  }, [defaultFileId, fetchFile]);

  return (
    <div className={containerClassName}>
      <TextField
        type="text"
        className={classes.inputContainer}
        label={label}
        placeholder={
          !isFetched ? (state.isUploading ? "Uploading..." : "Loading...") : ""
        }
        variant="outlined"
        disabled={!isFetched}
        InputLabelProps={{
          shrink: !!state.file || !isFetched
        }}
        InputProps={{
          readOnly: true,
          endAdornment: isFetched ? (
            !isShow && (
              <InputAdornment position="end" key="browseFileBtn">
                <label>
                  <input hidden type="file" onChange={handleSelectFile} />
                  <Button
                    component="span"
                    className={classes.browseButton}
                    color="primary"
                  >
                    <BrowseIcon color="action" />
                  </Button>
                </label>
              </InputAdornment>
            )
          ) : (
            <CircularProgress
              key="fetchingFile"
              size={32}
              thickness={2.5}
              className={classes.loading}
            />
          )
        }}
      />
      <div className={classes.chipContainer}>
        {state.file && isFetched && (
          <Chip
            key="fileChip"
            className={classes.chip}
            label={state.file.origName}
            onClick={
              isShow && state.file?.id
                ? async () => await downloadAndSave(state.file?.id || "")
                : undefined
            }
            onDelete={isShow ? undefined : handleRemoveFile}
            color="primary"
          />
        )}
      </div>
    </div>
  );
};

export default FileUpload;
